import ErrorMessageUtils from '../_helpers/error-message.util'
import AppConstants from '../constants/app.constants'
import { store } from '../store'

// パターンコードリスト
const patternCodeList = ['TABLE_CHECK_ERR_', 'FUNC_CHUTE_ERR_', 'FUNC_CREATE_DETAIL_DEVICE_ERR_', 'MASTER_SORTING_ERR_', 'WORK_TASK_ERR_', 'MASTER_TASK_TYPE_ERR_', 'MASTER_TASK_TYPE_SETTING_ERR_', 'FUNC_CREATE_DEVICE_ERR_']

/**
 * フラッシュメッセージコンポーネント。
 */
export default class FlashMessage {
  /**
   * FlashMessageComponentのインスタンスを取得します。.
   *
   * @param addToSuccessMsgListFunc グローバル成功マップに新しい成功メッセージを追加するために使用される関数
   * @param addToErrorMsgListFunc グローバルエラーメッセージマップに新しいエラーメッセージを追加するために使用される関数
   * @param removeAllErrorMessages グローバルエラーマップからすべてのエラーメッセージを削除するために使用される関数
   * @param removeAllSuccessMessages グローバルエラーマップからすべての成功メッセージを削除するために使用される関数
   * @param removeAllMessages グローバルからすべてのメッセージを削除するために使用される関数
   * @param removeDisconnectionErrorMessage グローバルから切断メッセージを削除するために使用される関数
   * @returns FlashMessageComponentインスタンス
   */
  static getInstance(
    addToSuccessMsgListFunc = null,
    addToErrorMsgListFunc = null,
    removeAllErrorMessages = null,
    removeAllSuccessMessages = null,
    removeAllMessages = null,
    removeDisconnectionErrorMessage = null
  ) {
    if (!this._messageComponent) {
      this._messageComponent = new FlashMessage()
      this._messageComponent._addToSuccessMsgListFunc = addToSuccessMsgListFunc
      this._messageComponent._addToErrorMsgListFunc = addToErrorMsgListFunc
      this._messageComponent._removeAllErrorMessages = removeAllErrorMessages
      this._messageComponent._removeAllSuccessMessages = removeAllSuccessMessages
      this._messageComponent._removeAllMessages = removeAllMessages
      this._messageComponent._removeDisconnectionErrorMessage = removeDisconnectionErrorMessage
    }
    return this._messageComponent
  }

  static isCreated() {
    if (this._messageComponent) {
      return true
    }
    return false
  }

  /**
   * 成功メッセージを表示します。
   *
   * @param msg 成功メッセージ
   * @returns Promise<void>
   */
  async showSuccessMessage(msg) {
    if (!this._addToSuccessMsgListFunc) {
      return
    }
    if (store.getters.getModalMode) {
      setTimeout(async () => {
        // リスト最下に新メッセージを表示
        await this._addToSuccessMsgListFunc(msg)
      }, 200)
      return
    }

    await store.commit('setImportMode', false)
    // リスト最下に新メッセージを表示
    await this._addToSuccessMsgListFunc(msg)
  }

  async hideAllErrorMessages() {
    // ストアからメッセージを削除する
    await this.removeAllErrorMessages()
    store.commit('setImportMode', false)
  }

  async hideAllSuccessMessages() {
    // ストアからすべての成功メッセージを削除する
    if (this._removeAllSuccessMessages) {
      await this._removeAllSuccessMessages()
    }
  }

  async hideAllMessages() {
    // ストアからメッセージを削除する
    if (this._removeAllMessages) {
      this.hideAllErrorMessages()
      await this._removeAllMessages()
    }
  }

  /**
   * 切断メッセージを非表示にする。
   */
  async hideDisconnectionErrorMessage() {
    if (this._removeDisconnectionErrorMessage) {
      await this._removeDisconnectionErrorMessage()
    }
  }

  async removeAllErrorMessages() {
    if (this._removeAllErrorMessages) {
      await this._removeAllErrorMessages()
    }
  }

  /**
   * エラーメッセージを表示します。
   *
   * @param {string} msg エラーメッセージ
   * @param {boolean} importMode インポートモード
   * @param {boolean} delayMode 遅延モード
   * @returns Promise<void>
   */
  async showErrorMessage(msg, importMode = false, delayMode = false) {
    if (!this._addToErrorMsgListFunc) {
      return
    }
    store.commit('setImportMode', importMode)

    if (delayMode) {
      setTimeout(async () => {
        // リスト最下に新メッセージを表示
        await this._addToErrorMsgListFunc(msg)
      }, 200)
      return
    }

    await this._addToErrorMsgListFunc(msg)
  }

  /**
   * エラーコードとエラーメッセージをフォーマットしてエラーメッセージを作成する。
   * @param {string} errCd はエラーコード
   * @param {string} errMsg はエラーメッセージです
   * @param {boolean} importMode インポートモード
   * @returns {string} エラーメッセージの形式
   */
  async showErrorMsgWithErrorCode(errCd, errMsg, importMode = false) {
    await this.showErrorMessage(ErrorMessageUtils.buildErrorMessage(errCd, errMsg), importMode)
  }

  /**
   * 切断エラーメッセージの表示。
   * @param {string} msg エラーメッセージ
   * @param {boolean} displayCloseIconErrMsgFlag   閉じるアイコン エラーメッセージ表示フラグ
   */
  async showDisconnectionErrorMsg(msg, displayCloseIconErrMsgFlag = false) {
    this.setDisplayCloseIconErrMsg(displayCloseIconErrMsgFlag)
    await this.showErrorMessage(msg)
  }

  /**
   *  切断エラーコード メッセージ表示。
   * @param {string} errCd エラーコード
   * @param {string} errMsg エラーメッセージ
   * @param {boolean} displayCloseIconErrMsgFlag 閉じるアイコン エラーメッセージ表示フラグ
   */
  async showDisconnectionErrorCdMsg(errCd, errMsg, displayCloseIconErrMsgFlag = false) {
    const errorMsg = ErrorMessageUtils.buildErrorMessage(errCd, errMsg)
    this.showDisconnectionErrorMsg(errorMsg, displayCloseIconErrMsgFlag)
  }

  /**
   * エラーメッセージを表示します。
   *
   * @param errorObject エラーオブジェクト
   * @returns Promise<void>
   */
  async parseAndShowErrorMessage(errorObject) {
    if (
      !errorObject ||
      !errorObject.response ||
      !errorObject.response.data ||
      !this._addToErrorMsgListFunc
    ) {
      return
    }

    let errObj = errorObject.response.data
    if (errObj instanceof Blob) {
      errObj = await this.parseBlobToJSON(errObj)
    }
    const errCd = errObj.errCd
    let errMsg = ''
    let errMsgs = []
    if (!errCd) {
      return
    }
    for (let idx = 0; idx < patternCodeList.length; idx++) {
      if (errCd.match(patternCodeList[idx])) {
        for (let key in errObj.errMap) {
          let errors = errObj.errMap[key]
          errors.forEach(errStr => {
            errMsgs.push(errStr)
          })
        }
      }
    }
    errMsg = errMsgs.join('') || errObj.errMsg
    await this._addToErrorMsgListFunc(errMsg)
  }

  /**
   * BlobからJSONへの値の解析
   *
   * @param {*} blob 解析するBlob値
   * @returns JSON値
   */
  async parseBlobToJSON(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        try {
          const json = JSON.parse(reader.result)
          resolve(json)
        } catch (error) {
          reject(error)
        }
      }
      reader.onerror = () => {
        reject(reader.error)
      }
      reader.readAsText(blob)
    })
  }

  /**
   * エラーメッセージ付きの閉じるアイコンを表示する
   */
  setDisplayCloseIconErrMsg(value) {
    store.commit('setCloseIconErrMsgMode', value)
  }

  /**
   * メッセージの高さを取得
   *
   * @param   {string} msg メッセージ
   * @returns {number}     メッセージの高さ
   */
  getHeightOfMessage(msg) {
    // 仮想要素を作成する
    let text = document.createElement('div')
    // アラート メッセージ要素のターゲットを取得する
    const elementParent = document.getElementById('alert-message-block') || document.body
    elementParent.appendChild(text)
    text.style.fontFamily = '"Helvetica Neue", Arial, "Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif'
    // 32pxはメッセージの左アイコン
    // 30pxはメッセージの右側のアイコンです
    text.style.width = `calc(
      100% - 32px
      ${store.getters.getCloseIconErrMsgMode ? ' - 30px' : ''}
      ${store.getters['settingWizardStore/getMenuStatus'] && !store.getters.getModalMode ? ` - ${AppConstants.SETTING_WIZARD_MENU_WIDTH}px` : ''}
    )`
    text.classList.add('virtual-alert-message')
    text.innerHTML = msg
    // 10pxはアラートメッセージのトップ/ボトムのパディング
    let msgHeight = Math.ceil(text.offsetHeight) + 10
    // 仮想要素を削除する
    elementParent.removeChild(text)
    // デフォルトの 1 行メッセージの高さは 43pxです
    return msgHeight < AppConstants.DEFAULT_MESSAGE_HEIGHT ? AppConstants.DEFAULT_MESSAGE_HEIGHT : msgHeight
  }
}



// WEBPACK FOOTER //
// ./src/common/flash-message.component.js